import {
  BattedBallType,
  ContactQuality,
  SessionHitResult,
} from 'enums/session-recap/enums';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { OutcomeCode } from 'lib_ts/enums/hitters.enums';
import { PitchType } from 'lib_ts/enums/pitches.enums';

export type RGB = [number, number, number];

const COLD_HEX = '#356EE6';
export const NEUTRAL_RGB_HEX = '#8B8D98';
const HOT_HEX = '#E5484D';

export const COLD_RGB = StringHelper.hexToRgb(COLD_HEX) as RGB;
export const NEUTRAL_RGB = StringHelper.hexToRgb(NEUTRAL_RGB_HEX) as RGB;
export const HOT_RGB = StringHelper.hexToRgb(HOT_HEX) as RGB;

export const DIVERGING_GRADIENT = [COLD_HEX, NEUTRAL_RGB_HEX, HOT_HEX];

/** jet scale used by the plate heatmap: low (blue) through green to high (red) */
export const HEATMAP_GRADIENT = [
  '#0033EE',
  '#00B4FF',
  '#2EC84A',
  '#FFE600',
  '#F02D1F',
];

export const HEATMAP_RGB = HEATMAP_GRADIENT.map(
  (hex) => StringHelper.hexToRgb(hex) as RGB
);

export const SESSION_HIT_RESULT_COLORS: Record<SessionHitResult, string> = {
  [SessionHitResult.Single]: HitHelper.getOutcomeColor(OutcomeCode.Single),
  [SessionHitResult.Double]: HitHelper.getOutcomeColor(OutcomeCode.Double),
  [SessionHitResult.Triple]: HitHelper.getOutcomeColor(OutcomeCode.Triple),
  [SessionHitResult.HomeRun]: HitHelper.getOutcomeColor(OutcomeCode.HomeRun),
  [SessionHitResult.FlyOut]: HitHelper.getOutcomeColor(OutcomeCode.FlyOut),
  [SessionHitResult.GroundOut]: HitHelper.getOutcomeColor(
    OutcomeCode.GroundOut
  ),
  [SessionHitResult.Foul]: HitHelper.getOutcomeColor(OutcomeCode.Foul),
  [SessionHitResult.SwingingStrike]: HitHelper.getOutcomeColor(
    OutcomeCode.SwingingStrike
  ),
  [SessionHitResult.CalledStrike]: HitHelper.getOutcomeColor(
    OutcomeCode.CalledStrike
  ),
  [SessionHitResult.Ball]: HitHelper.getOutcomeColor(OutcomeCode.Ball),
  [SessionHitResult.FoulOut]: HitHelper.getOutcomeColor(OutcomeCode.FoulOut),
};

export const CONTACT_QUALITY_COLORS: Record<ContactQuality, string> = {
  [ContactQuality.Weak]: NEUTRAL_RGB_HEX,
  [ContactQuality.Topped]: '#0090FF',
  [ContactQuality.Under]: '#12A594',
  [ContactQuality.FlareBurner]: '#FFC53D',
  [ContactQuality.Solid]: '#F76B15',
  [ContactQuality.Barrel]: '#E5484D',
};

export const BATTED_BALL_TYPE_COLORS: Record<BattedBallType, string> = {
  [BattedBallType.GroundBall]: '#AD7F58',
  [BattedBallType.LineDrive]: '#30A46C',
  [BattedBallType.FlyBall]: '#0090FF',
  [BattedBallType.Popup]: '#8E4EC6',
};

export const PITCH_TYPE_PALETTE = [
  '#E5484D',
  '#0090FF',
  '#30A46C',
  '#FFC53D',
  '#8E4EC6',
  '#F76B15',
  '#12A594',
  '#D6409F',
  '#AD7F58',
  '#3E63DD',
  '#BDEE63',
  '#7CE2FE',
];

/** fixed so a pitch type keeps its colour across sessions */
export const PITCH_TYPE_ORDER: PitchType[] = [
  PitchType.FourSeamFastball,
  PitchType.Sinker,
  PitchType.Cutter,
  PitchType.Slider,
  PitchType.Curveball,
  PitchType.ChangeUp,
  PitchType.Splitter,
  PitchType.Sweeper,
  PitchType.KnuckleCurve,
  PitchType.TwoSeamFastball,
  PitchType.Slurve,
  PitchType.Forkball,
];
